import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux";
import PropTypes from "prop-types"
import {
    IconButton,
    Button,
    Chip,
    Menu,
    MenuItem
} from "@mui/material"
import {
    LocationOn,
    CalendarMonth,
    WatchLater,
    HourglassBottom,
    MoreVert,
    Share,
    Flag,
    Favorite,
    FavoriteBorder,
    ConfirmationNumber,
    Download
} from '@mui/icons-material';
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useEventStatus } from "../../hooks/event-hooks/useEventStatus";
import Menus from "../Dialog/Menus"
import EventSharePopup from "../EventMetaDescription/EventSharePopup";
import { ShmoozShadowLogo } from "../../assets/images"
import { H3, Body2, Body3, H6 } from "../../themes/typography";
dayjs.extend(duration);


const thumbnailUrl =
    'https://images.unsplash.com/photo-1728357728879-ac9a5c703c2c?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxmZWF0dXJlZC1waG90b3MtZmVlZHw5fHx8ZW58MHx8fHx8';

function EventDetailBanner({
    thumbnail = thumbnailUrl,
    name,
    address,
    minTicketPrice,
    date,
    eventType,
    schedule,
    time,
    fcs,
    duration,
    getTickets = () => { },
    showTickets,
    bookingId,
    noOfTicketsBooked,
    showElipsisMenu = true,
    showGetTicketButton = true,
    showDownloadTicketButton = false,
    openInvoiceMenu,
    anchorElInvoiceMenu,
    openInvoiceMenuDropdown,
    closeInvoiceMenu,
    downloadTickets,
    downloadInvoice,
    status,
    handleInterestClick,
    isInterested
}) {

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [openEventSharePopup, setOpenEventSharePopup] = useState(false);

    const { inviteLinkUserDetails } = useSelector((state) => state.invitation);

    // console.log("Invite -> ", inviteLinkUserDetails);

    const [countdown, setCountdown] = useState("");

    const {
        isEventScheduled,
        isEventEnded,
        isEventCancelled,
        isEventLive,
        isEventSingleDay,
        isEventMultiDay,
        isEventContinuous
    } = useEventStatus();

    function handleClose() {
        setAnchorEl(null);
    }

    function handleClick(event) {
        setAnchorEl(event.currentTarget);
    }

    function handleOpenEventSharePopup() {
        setOpenEventSharePopup(true);
    }

    function handleCloseEventSharePopup() {
        setOpenEventSharePopup(false);
    }


    const menusList = [
        {
            id: 'interest',
            icon: isInterested ? (
                <Favorite style={{ color: 'var(--tertiary-color)', fontSize: '1.5rem' }} />
            ) : (
                <FavoriteBorder style={{ color: 'var(--tertiary-color)', fontSize: '1.5rem' }} />
            ),
            menuName: isInterested ? 'Interested' : 'Interest',
            onClick: handleInterestClick
        },
        {
            id: 'share',
            icon: <Share style={{ color: 'var(--tertiary-color)', fontSize: '1.5rem' }} />,
            menuName: 'Share',
            onClick: handleOpenEventSharePopup
        },
        {
            id: 'report',
            icon: <Flag style={{ color: 'var(--tertiary-color)', fontSize: '1.5rem' }} />,
            menuName: 'Report an issue',
            link: '/report-event'
        }
    ];


    /**
     * Countdown logic for each event type -> single day, multi day, continuous
     */
    useEffect(() => {

        /**
         * For single day event type only
         */
        if (isEventScheduled && isEventSingleDay && schedule?.length > 0) {
            /**
             * Get the first schedule item
             */
            const firstSchedule = schedule[0];

            /**
             * Make event start date time by combining date and from_time
             */
            const eventStart = dayjs(`${firstSchedule.date} ${firstSchedule.from_time}`);

            /**
             * Set interval to update countdown every second
             */
            const interval = setInterval(() => {
                /**
                 * Get current date time
                 */
                const now = dayjs();

                /**
                 * Get the difference between event start date time and current date time in milliseconds
                 */
                const diffInMilliSeconds = eventStart.diff(now);

                /**
                 * If difference is less than or equal to zero, clear the interval
                 */
                if (diffInMilliSeconds <= 0) {
                    // setCountdown("Starting soon...");
                    clearInterval(interval);
                    return;
                }

                /**
                 * Convert the difference in milliseconds to duration (months, days, hours, minutes, seconds) by passing it to dayjs.duration()
                 */
                const diff = dayjs.duration(diffInMilliSeconds);

                /**
                 * 1. If months > 0, show months, days, hours, minutes
                 * 2. If days > 0, show days, hours, minutes
                 * 3. If hours > 0, show hours, minutes
                 * 4. Else show only minutes
                 */
                if (diff.months() > 0) {
                    setCountdown(
                        `${diff.months()}m ${diff.days()}d ${diff.hours()}h ${diff.minutes()}m`
                    );
                } else if (diff.days() > 0) {
                    setCountdown(
                        `${diff.days()}d ${diff.hours()}h ${diff.minutes()}m`
                    );
                } else if (diff.hours() > 0) {
                    setCountdown(
                        `${diff.hours()}h ${diff.minutes()}m`
                    );
                } else {
                    setCountdown(`${diff.minutes()}m`);
                }
            }, 1000);

            /**
             * Clear the interval on component unmount
             */
            return () => clearInterval(interval);
        }


        /**
         * For multi day event type only
         */
        if ((isEventScheduled || isEventLive) && isEventMultiDay && schedule?.length > 0) {

            /**
             * Sort schedules by start time
             */
            const sortedSchedules = [...schedule].sort((a, b) => {
                const dateTimeA = dayjs(`${a.date} ${a.from_time}`);
                const dateTimeB = dayjs(`${b.date} ${b.from_time}`);
                return dateTimeA.diff(dateTimeB);
            });


            /**
             * Variable to hold the next upcoming schedule
             */
            let upcomingSchedule = null;


            /**
             * Set interval to update countdown every second
             */
            const interval = setInterval(() => {
                const now = dayjs();

                /**
                 * Check if any schedule is currently active (now is between from_time and to_time)
                 */
                const activeSchedule = sortedSchedules.find(s => {
                    const start = dayjs(`${s.date} ${s.from_time}`);
                    const end = dayjs(`${s.date} ${s.to_time}`);
                    return now.isAfter(start) && now.isBefore(end);
                });


                /**
                 * If there's an active schedule, set countdown to "live" and return (skip further processing)
                 */
                if (activeSchedule) {
                    setCountdown("live"); // special flag to show in ui
                    return;
                }


                /**
                 * Find the next upcoming schedule (the first schedule whose start time is after now)
                 */
                upcomingSchedule = sortedSchedules.find(s => {
                    const start = dayjs(`${s.date} ${s.from_time}`);
                    return start.isAfter(now);
                });


                /**
                 * If there's an upcoming schedule, calculate the difference and set the countdown
                 */
                if (upcomingSchedule) {

                    const nextEventStart = dayjs(`${upcomingSchedule.date} ${upcomingSchedule.from_time}`);
                    const diffInMilliSeconds = nextEventStart.diff(now);

                    /**
                     * If difference is less than or equal to zero, skip this tick (will re-check in next tick)
                     */
                    if (diffInMilliSeconds <= 0) {
                        return;
                    }


                    /**
                     * Convert the difference in milliseconds to duration (months, days, hours, minutes, seconds) by passing it to dayjs.duration()
                     */
                    const diff = dayjs.duration(diffInMilliSeconds);


                    /**
                     * 1. If months > 0, show months, days, hours, minutes
                     * 2. If days > 0, show days, hours, minutes
                     * 3. If hours > 0, show hours, minutes
                     * 4. Else show only minutes
                     * 
                     * else -> event has ended (no more schedules left)
                     */
                    if (diff.months() > 0) {
                        setCountdown(`${diff.months()}m ${diff.days()}d ${diff.hours()}h ${diff.minutes()}m`);
                    } else if (diff.days() > 0) {
                        setCountdown(`${diff.days()}d ${diff.hours()}h ${diff.minutes()}m`);
                    } else if (diff.hours() > 0) {
                        setCountdown(`${diff.hours()}h ${diff.minutes()}m`);
                    } else {
                        setCountdown(`${diff.minutes()}m`);
                    }
                } else {
                    // No more schedules left
                    setCountdown("ended"); // another special flag
                    clearInterval(interval);
                }
            }, 1000);


            /**
             * Clear the interval on component unmount
             */
            return () => clearInterval(interval);
        }


        /**
         * For continuous event type only
         */
        if (isEventScheduled && isEventContinuous && schedule?.length > 0) {

            /**
             * Get the first schedule item
             */
            const firstSchedule = schedule[0];

            /**
             * Make event start date time by combining date and from_time
             */
            const eventStart = dayjs(`${firstSchedule.date} ${firstSchedule.from_time}`);

            /**
             * Set interval to update countdown every second
             */
            const interval = setInterval(() => {

                /**
                 * Get current date time
                 */
                const now = dayjs();

                /**
                 * Get the difference between event start date time and current date time in milliseconds
                 */
                const diffInMilliSeconds = eventStart.diff(now);

                /**
                 * If difference is less than or equal to zero, clear the interval
                 */
                if (diffInMilliSeconds <= 0) {
                    // setCountdown("Starting soon...");
                    clearInterval(interval);
                    return;
                }

                /**
                 * Convert the difference in milliseconds to duration (months, days, hours, minutes, seconds) by passing it to dayjs.duration()
                 */
                const diff = dayjs.duration(diffInMilliSeconds);


                /**
                 * 1. If months > 0, show months, days, hours, minutes
                 * 2. If days > 0, show days, hours, minutes
                 * 3. If hours > 0, show hours, minutes
                 * 4. Else show only minutes
                 */
                if (diff.months() > 0) {
                    setCountdown(
                        `${diff.months()}m ${diff.days()}d ${diff.hours()}h ${diff.minutes()}m`
                    );
                } else if (diff.days() > 0) {
                    setCountdown(
                        `${diff.days()}d ${diff.hours()}h ${diff.minutes()}m`
                    );
                } else if (diff.hours() > 0) {
                    setCountdown(
                        `${diff.hours()}h ${diff.minutes()}m`
                    );
                } else {
                    setCountdown(`${diff.minutes()}m`);
                }
            }, 1000);


            /**
             * Clear the interval on component unmount
             */
            return () => clearInterval(interval);
        }

    }, [status, eventType, schedule]);

    // console.log("countdown", countdown);


    return (
        <React.Fragment>
            <div
                className="main-event-detail-banner-container"
            >


                {isEventScheduled && countdown && countdown !== "live" && countdown !== "ended" && (
                    <div className="event-live-status-container" style={{ backgroundColor: 'var(--info-main-color)' }}>
                        <H6 fontWeight={600}>The event will begin in {countdown}</H6>
                    </div>
                )}

                {countdown === "live" && (
                    <div className="event-live-status-container" style={{ backgroundColor: 'var(--success-main-color)' }}>
                        <H6 fontWeight={600}>Event is live</H6>
                    </div>
                )}


                {isEventLive && (
                    <div
                        className="event-live-status-container"
                        style={{
                            backgroundColor: 'var(--success-main-color)'
                        }}
                    >
                        <H6 fontWeight={600}>
                            {inviteLinkUserDetails?.status === 'ongoing'
                                ? inviteLinkUserDetails?.errorText
                                : 'Event is live'
                            }
                        </H6>
                    </div>
                )}

                {isEventCancelled && (
                    <div
                        className="event-live-status-container"
                        style={{
                            backgroundColor: 'var(--error-secondary-color)'
                        }}
                    >
                        <H6 fontWeight={600}>
                            {inviteLinkUserDetails?.status === 'cancelled'
                                ? inviteLinkUserDetails?.errorText
                                : 'Event has been cancelled'
                            }
                        </H6>
                    </div>
                )}

                {isEventEnded && (
                    <div
                        className="event-live-status-container"
                        style={{
                            backgroundColor: 'var(--error-secondary-color)'
                        }}
                    >
                        <H6 fontWeight={600}>
                            {inviteLinkUserDetails?.status === 'event_ended'
                                ? inviteLinkUserDetails?.errorText
                                : 'Event has ended'
                            }
                        </H6>
                    </div>
                )}

                <div className="event-basic-detail-banner">

                    <div className="event-banner-thumbnail-image">
                        <img src={thumbnail} alt="Event" />
                    </div>

                    <div className="event-banner-basic-details">

                        <div className="event-name-banner">
                            <H3 color="var(--secondary-color)" fontWeight={700}>
                                {name}
                            </H3>
                        </div>

                        <div className="event-schedule-details-container">

                            <div className="event-basic-main-schedule-container">

                                <div className="event-basic-main-schedule-left">

                                    <div className="event-location-format-price-details">

                                        {address === null
                                            ? (
                                                <div className="event-basic-location">
                                                    <LocationOn sx={iconStyle} />
                                                    <Body2>
                                                        To be announced soon
                                                    </Body2>
                                                </div>
                                            ) : (
                                                <div className="event-basic-location">
                                                    <LocationOn sx={iconStyle} />
                                                    <Body2>
                                                        {address}
                                                    </Body2>
                                                </div>
                                            )
                                        }

                                        {fcs && fcs.length > 0 && (
                                            <div className="event-basic-tags">
                                                {fcs?.map((tag, index) => (
                                                    <CustomChip key={index} name={tag?.name} />
                                                ))}
                                            </div>
                                        )}

                                        {minTicketPrice != null && !showTickets && (
                                            <div className="event-starting-price">
                                                {minTicketPrice > 0
                                                    && (
                                                        <Body2>
                                                            From{' '}
                                                            <span style={{ color: 'var(--secondary-color)' }}>
                                                                ₹{minTicketPrice}
                                                            </span>{' '}
                                                            Onwards
                                                        </Body2>
                                                    )
                                                }

                                                {minTicketPrice === 0 && (
                                                    <Body2>Free Entry</Body2>
                                                )}
                                            </div>
                                        )}

                                        {bookingId && (
                                            <div className="event-starting-price">
                                                <Body2>
                                                    Booking Id: {' '}
                                                    <span style={{ color: 'var(--secondary-color)' }}>
                                                        {bookingId}
                                                    </span>
                                                </Body2>
                                            </div>
                                        )}


                                    </div>
                                </div>

                                <div className="event-basic-main-right-side">

                                    {date && (
                                        <div className="event-date-detail">
                                            <CalendarMonth sx={iconStyleWithoutMargin} />
                                            <Body2>
                                                {date}
                                            </Body2>
                                        </div>
                                    )}

                                    {time && (
                                        <div className="event-date-detail">
                                            <WatchLater sx={iconStyleWithoutMargin} />
                                            <Body2>
                                                {time}
                                            </Body2>
                                        </div>
                                    )}

                                    {duration && (
                                        <div className="event-date-detail">
                                            <HourglassBottom sx={iconStyleWithoutMargin} />
                                            <Body2>
                                                {duration}
                                            </Body2>
                                        </div>
                                    )}

                                    {noOfTicketsBooked && (
                                        <div className="event-date-detail">
                                            <ConfirmationNumber sx={iconStyleWithoutMargin} />
                                            <Body2>
                                                Number of tickets: {noOfTicketsBooked}
                                            </Body2>
                                        </div>
                                    )}

                                </div>

                            </div>

                            <div className="preview-event-get-tickets-button">
                                {showGetTicketButton && (
                                    <div>
                                        <Button variant="main" onClick={getTickets}>
                                            {showTickets ? 'View Event' : 'Get Tickets'}
                                        </Button>
                                    </div>
                                )}

                                {showDownloadTicketButton && (
                                    <div>
                                        <Button
                                            variant="toggleActive"
                                            endIcon={<Download />}
                                            onClick={downloadTickets}
                                        >
                                            Tickets
                                        </Button>
                                    </div>
                                )}

                                {showDownloadTicketButton && (
                                    <div>
                                        <Button
                                            variant="toggleActive"
                                            endIcon={<Download />}
                                            onClick={openInvoiceMenuDropdown}
                                            aria-controls={openInvoiceMenu ? 'invoice-positioned-menu' : undefined}
                                            aria-haspopup="true"
                                            aria-expanded={openInvoiceMenu ? 'true' : undefined}
                                        >
                                            Invoice
                                        </Button>
                                        <Menu
                                            id="demo-positioned-menu"
                                            aria-labelledby="demo-positioned-button"
                                            anchorEl={anchorElInvoiceMenu}
                                            open={openInvoiceMenu}
                                            onClose={closeInvoiceMenu}
                                            anchorOrigin={{
                                                vertical: 'top',
                                                horizontal: 'left',
                                            }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'left',
                                            }}
                                        >
                                            <MenuItem
                                                onClick={downloadInvoice}
                                            >
                                                Ticket Invoice
                                            </MenuItem>
                                            <MenuItem
                                                onClick={downloadInvoice}
                                            >
                                                Booking Invoice
                                            </MenuItem>
                                        </Menu>
                                    </div>
                                )}
                            </div>

                        </div>

                    </div>

                    <div className="event-banner-shmooz-shadow">

                        {showElipsisMenu && (
                            <div className="event-banner-elipsis-menu">
                                <IconButton
                                    color="elipsis"
                                    onClick={handleClick}
                                    aria-controls={open ? 'event-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                >
                                    <MoreVert sx={{ fontSize: '2rem' }} />
                                </IconButton>
                                <Menus
                                    id="event-menus"
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClick={handleClose}
                                    onClose={handleClose}
                                    menus={menusList}
                                    shouldDisabledMenus={false}
                                />
                            </div>
                        )}


                        <div className="event-banner-shmooz-shadow-logo">
                            <img src={ShmoozShadowLogo} alt="Shmooz" />
                        </div>
                    </div>

                </div>

            </div>

            <EventSharePopup
                open={openEventSharePopup}
                onClose={handleCloseEventSharePopup}
            // shareEventName={name}
            />
        </React.Fragment>
    )
}

export default EventDetailBanner

const iconStyle = {
    color: 'var(--tertiary-color)',
    fontSize: '1.25rem',
    marginTop: '2px'
};

const iconStyleWithoutMargin = {
    color: 'var(--tertiary-color)',
    fontSize: '1.25rem',
    marginTop: '0px'
};

function CustomChip({ name = 'Travel' }) {
    return (
        <Chip
            label={
                <Body3 fontWeight={500} color="var(--tertiary-color)">
                    {name}
                </Body3>
            }
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                border: '1px solid var(--quaternary-color)',
                padding: '0px 14px',
                minHeight: '24px',
                maxHeight: '44px',
                borderRadius: '1.5rem',
                boxSizing: 'border-box',
                textTransform: 'capitalize',
                backgroundColor: 'transparent',
                '& .MuiChip-label': {
                    padding: '0px'
                }
            }}
        />
    );
}


/**
 * Prop type validation for EventDetailBanner component
 */
EventDetailBanner.propTypes = {
    name: PropTypes.string,
    address: PropTypes.string,
    date: PropTypes.string,
    time: PropTypes.string,
    fcs: PropTypes.array,
    getTickets: PropTypes.func,
    showTickets: PropTypes.bool,
    showElipsisMenu: PropTypes.bool,
    showGetTicketButton: PropTypes.bool,
    showDownloadTicketButton: PropTypes.bool,
    openInvoiceMenu: PropTypes.bool,
    anchorElInvoiceMenu: PropTypes.object,
    openInvoiceMenuDropdown: PropTypes.func,
    closeInvoiceMenu: PropTypes.func,
    downloadTickets: PropTypes.func,
    downloadInvoice: PropTypes.func,
    noOfTicketsBooked: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    minTicketPrice: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    bookingId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    duration: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
}